import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function HeartyBreakfastBowls() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "hearty-breakfast-bowls-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-jam-cafe": "Jam Cafe",
    "section-2-oeb": "OEB Breakfast Co.",
    "section-3-burrow": "The Burrow",
    "section-4-roots": "Roots Cafe",
    "section-5-tractor": "Tractor Foods",
    "section-6-glory-juice": "Glory Juice Co.",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism",
    },
    {
      url: "https://www.vch.ca/en/health-professionals/professionals-food-safety/food-safety-inspections",
      label: "Vancouver Coastal Health – Restaurant Inspections",
    },
    {
      url: "https://vancouver.ca/greencity",
      label: "City of Vancouver – Sustainability Initiatives",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match
  // the ref you use in the corresponding <p> or <li> section.
  const locations = [
    // Jam Cafe
    {
      id: "section-1-jam-cafe",
      lat: 49.28153,
      lng: -123.11023,
      popupText: "Jam Cafe (556 Beatty St)",
    },
    // OEB Breakfast Co.
    {
      id: "section-2-oeb",
      lat: 49.27346,
      lng: -123.11927,
      popupText: "OEB Breakfast Co. (1137 Marinaside Crescent)",
    },
    // The Burrow
    {
      id: "section-3-burrow",
      lat: 49.26300,
      lng: -123.06950,
      popupText: "The Burrow (2781 Commercial Dr)",
    },
    // Roots Cafe
    {
      id: "section-4-roots",
      lat: 49.22500,
      lng: -123.10100,
      popupText: "Roots Cafe (6502 Main St)",
    },
    // Tractor Foods
    {
      id: "section-5-tractor",
      lat: 49.26300,
      lng: -123.12100,
      popupText: "Tractor Foods (601 West Broadway)",
    },
    // Glory Juice Co.
    {
      id: "section-6-glory-juice",
      lat: 49.27500,
      lng: -123.12000,
      popupText: "Glory Juice Co. (437 Davie St)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Hearty Breakfast Bowls in Vancouver: A Healthy Start</title>
        <meta
          name="description"
          content="Explore Vancouver’s best hearty breakfast bowls. From indulgent comfort food to plant-based power bowls, discover top spots, FAQs, tips, and more."
        />
        <meta
          name="keywords"
          content="Vancouver, breakfast bowls, brunch spots, healthy eating, restaurants"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/hearty-breakfast-bowls-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Hearty Breakfast Bowls in Vancouver: A Healthy Start"
        />
        <meta
          property="og:description"
          content="Explore Vancouver’s best hearty breakfast bowls. From indulgent comfort food to plant-based power bowls, discover top spots, FAQs, tips, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/hearty-breakfast-bowls-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/hearty-breakfast-bowls-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Hearty Breakfast Bowls in Vancouver: A Healthy Start"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver’s best hearty breakfast bowls. From indulgent comfort food to plant-based power bowls, discover top spots, FAQs, tips, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/hearty-breakfast-bowls-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Hearty breakfast bowl with fresh ingredients"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              A satisfying breakfast is the key to starting your day on the
              right foot. In Vancouver, a city admired for its vibrant culinary
              scene and health-conscious culture,{" "}
              <strong>hearty breakfast bowls</strong> have become a beloved
              staple. Combining proteins, grains, vegetables, and sauces in a
              single dish, these bowls offer flavor, convenience, and plenty of
              nourishment for any busy morning. This article highlights some of
              the top spots in Vancouver where you can indulge in a
              soul-satisfying breakfast bowl—whether you crave comfort-food
              classics or lighter, plant-based options.
            </p>

            {/* SECTION: Jam Cafe */}
            <h2 id="section-1-jam-cafe">Jam Cafe</h2>
            <p>
              Famous for its all-day brunch and generous portions,{" "}
              <strong>Jam Cafe</strong> serves up comfort-food style bowls that
              never skimp on flavor. The popular “Charlie Bowl” is a
              mouthwatering mix of crumbled biscuit, hash browns, ham, bacon,
              cheese, and sausage gravy, topped with two perfectly poached eggs.
              Vegetarian options are also available, offering roasted veggies
              smothered in mushroom gravy and cheese. Prepare to wait in line at
              busy times—this spot is walk-in only and known to be a local
              favorite.
            </p>
            <p
              ref={(el) => setSpotRef("section-1-jam-cafe", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.28153}
                coordX={-123.11023}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>556 Beatty St, Vancouver, BC V6B 2L3</em> – Centrally located
              in Downtown Vancouver.
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> $15–$20 per bowl
              </li>
              <li>
                <strong>Operating Hours:</strong> Weekdays 8:00am – 2:30pm;
                Weekends 8:00am – 3:00pm
              </li>
              <li>
                <strong>Standout Features:</strong> Southern-inspired comfort,
                large portions, family-recipe gravies
              </li>
            </ul>

            {/* SECTION: OEB Breakfast Co. */}
            <h2 id="section-2-oeb">OEB Breakfast Co.</h2>
            <p>
              <strong>OEB Breakfast Co.</strong> elevates the breakfast game
              with its chef-driven approach. Their signature “Soul in a Bowl”
              packs duck-fat fried potatoes, bacon lardons, cheese curds, brown
              butter hollandaise, and poached eggs into an indulgent morning
              feast. The atmosphere is bright and modern, perfect for a hearty
              weekend brunch or an upscale weekday treat. OEB often has a
              waitlist, so consider arriving early or using a virtual queue
              system when available.
            </p>
            <p
              ref={(el) => setSpotRef("section-2-oeb", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27346}
                coordX={-123.11927}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>1137 Marinaside Crescent, Vancouver, BC V6Z 2Y3</em>
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> $20–$25 per bowl (premium
                ingredients)
              </li>
              <li>
                <strong>Operating Hours:</strong> 7:00am – 3:00pm daily
              </li>
              <li>
                <strong>Standout Features:</strong> Breakfast “poutine”-style
                bowls, duck-fat fried potatoes, locally sourced ingredients
              </li>
            </ul>

            {/* SECTION: The Burrow */}
            <h2 id="section-3-burrow">The Burrow</h2>
            <p>
              Formerly known as Bandidas Taqueria, <strong>The Burrow</strong>{" "}
              offers vegetarian and vegan Mexican-inspired fare, including a
              memorable “Willow Bowl” packed with roasted yams, poblano peppers,
              potatoes, and a tofu scramble smothered in cashew queso. This
              hearty bowl is perfect for plant-based diners craving substantial
              flavor without meat or dairy. The cozy and artsy ambiance fits
              right into Commercial Drive’s eclectic vibe.
            </p>
            <p
              ref={(el) => setSpotRef("section-3-burrow", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.26300}
                coordX={-123.06950}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>2781 Commercial Drive, Vancouver, BC V5N 4C5</em>
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> $13–$18 per bowl
              </li>
              <li>
                <strong>Operating Hours (Brunch):</strong> Sat–Sun 10:00am –
                3:00pm
              </li>
              <li>
                <strong>Standout Features:</strong> 100% vegetarian with many
                vegan &amp; gluten-free options, casual community vibe
              </li>
            </ul>

            {/* SECTION: Roots Cafe */}
            <h2 id="section-4-roots">Roots Cafe</h2>
            <p>
              <strong>Roots Cafe</strong> in the Sunset neighborhood is a
              friendly, budget-conscious choice. Its classic Breakfast Bowl
              layers crispy hash browns, avocado, pickled onions, tomatoes, and
              eggs under a blanket of house-made hollandaise—hearty and
              affordable. This spot is beloved by locals for its laid-back vibe,
              local art displays, and commitment to homestyle cooking.
            </p>
            <p
              ref={(el) => setSpotRef("section-4-roots", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.22500}
                coordX={-123.10100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>6502 Main Street, Vancouver, BC V5X 3G9</em>
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> ~$15 per bowl
              </li>
              <li>
                <strong>Operating Hours:</strong> Mon–Fri 8:00am – 8:00pm; Sat
                9:00am – 6:00pm; Sun 9:00am – 5:00pm
              </li>
              <li>
                <strong>Standout Features:</strong> Homemade sauces, family-run
                atmosphere, vegetarian-friendly
              </li>
            </ul>

            {/* SECTION: Tractor Foods */}
            <h2 id="section-5-tractor">Tractor Foods</h2>
            <p>
              For a lighter, health-focused twist on breakfast bowls,{" "}
              <strong>Tractor Foods</strong> is a local mini-chain offering
              balanced combos of eggs (or tofu), greens, veggies, and grains.
              Their “Farmhouse Breakfast Bowl,” featuring scrambled eggs, bacon
              or turkey sausage, cheese, and breakfast potatoes on a bed of
              arugula, is a crowd-pleaser. Vegan and vegetarian bowls are also
              available, making Tractor a convenient choice for anyone seeking a
              wholesome start to the day.
            </p>
            <p
              ref={(el) => setSpotRef("section-5-tractor", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.26300}
                coordX={-123.12100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>601 West Broadway, Vancouver, BC V5Z 4C2</em>
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> $12–$15 per bowl
              </li>
              <li>
                <strong>Operating Hours:</strong> 7:00am – 9:30pm (breakfast
                served until ~10:30am)
              </li>
              <li>
                <strong>Standout Features:</strong> Health-forward menu with
                marked vegan/gluten-free options, quick service
              </li>
            </ul>

            {/* SECTION: Glory Juice Co. */}
            <h2 id="section-6-glory-juice">Glory Juice Co.</h2>
            <p>
              If you prefer plant-based and organic,{" "}
              <strong>Glory Juice Co.</strong> has acai bowls, tofu scrambles,
              and smoothie bowls loaded with superfoods. Their Tofu Benny Bowl
              is a vegan delight that mimics the flavors of eggs Benedict with
              cruelty-free ingredients. This modern, airy juice bar in Yaletown
              is perfect for a quick morning pick-me-up that won’t leave you
              feeling weighed down.
            </p>
            <p
              ref={(el) => setSpotRef("section-6-glory-juice", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.27500}
                coordX={-123.12000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              <em>437 Davie Street, Vancouver, BC V6B 2G2</em>
            </p>
            <ul>
              <li>
                <strong>Price Range:</strong> $11–$14
              </li>
              <li>
                <strong>Operating Hours:</strong> Mon–Fri 7:00am – 7:00pm;
                Sat–Sun 8:00am – 7:00pm
              </li>
              <li>
                <strong>Standout Features:</strong> 100% organic and vegan,
                sustainability-focused, acai and smoothie bowls
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              All Vancouver restaurants must comply with Vancouver Coastal
              Health regulations, which include rigorous food handling practices
              and permitting. Look for a valid food service permit when dining
              out. The city also has eco-friendly initiatives like bans on
              certain single-use plastics and a push for compostable containers.
              Most local eateries embrace these practices, so you’ll often find
              paper straws and compostable takeout items.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is a breakfast bowl?</h3>
              <p>
                A breakfast bowl is a complete morning meal served in a bowl,
                typically combining proteins (like eggs, tofu, or meat),
                vegetables, grains, and sauces. They can range from indulgent,
                gravy-laden creations to health-conscious smoothies and acai.
              </p>

              <h3>2. Are there vegan or vegetarian breakfast bowl options?</h3>
              <p>
                Definitely. Many Vancouver spots are accommodating to vegans and
                vegetarians. Places like The Burrow and Glory Juice Co. specialize
                in entirely vegetarian or vegan bowls.
              </p>

              <h3>3. How much do these bowls typically cost?</h3>
              <p>
                Prices vary from about $12 at budget-friendly cafes to $20+ for
                premium, gourmet bowls with specialty ingredients. Expect to pay
                around $15 on average.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From Southern-inspired comfort at Jam Cafe to plant-based bliss at
              Glory Juice Co., Vancouver offers a wide variety of hearty
              breakfast bowls to suit every taste and dietary need. These bowls
              capture the essence of the city’s culinary diversity—fresh,
              flavorful, and mindful of various food preferences. Whether you’re
              starting your day with a decadent “breakfast poutine” or a
              nutrient-packed tofu scramble, you can count on Vancouver’s bowl
              scene to deliver. Now, it’s time to explore these spots and treat
              yourself to a hearty start!
            </p>
            <p>
              <strong>Disclaimer:</strong> Always check official resources for
              updated menus, hours, and safety guidelines. If you have
              dietary-related medical concerns, consult restaurant staff or your
              healthcare provider.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}